import { useRef, useState } from "react";
import type { KeyboardEvent, PointerEvent, ReactNode } from "react";
import { Tabs } from "../primitives/Tabs";
import type { TabItem } from "../primitives/Tabs";

export type SheetSnap = "peek" | "half" | "full";

export const sheetSnaps: SheetSnap[] = ["peek", "half", "full"];

export type BottomSheetProps<T extends string> = {
  tabs: TabItem<T>[];
  tab: T;
  onTabChange: (tab: T) => void;
  snap: SheetSnap;
  onSnapChange: (snap: SheetSnap) => void;
  label: string;
  tabsLabel: string;
  handleLabel: string;
  children: (tab: T) => ReactNode;
  header?: ReactNode;
  className?: string;
};

export function nextSnap(current: SheetSnap, direction: 1 | -1): SheetSnap {
  const index = sheetSnaps.indexOf(current);

  return sheetSnaps[Math.min(sheetSnaps.length - 1, Math.max(0, index + direction))];
}

export function snapFromDrag(current: SheetSnap, deltaY: number, threshold = 60): SheetSnap {
  if (deltaY > threshold) return nextSnap(current, -1);
  if (deltaY < -threshold) return nextSnap(current, 1);

  return current;
}

export function BottomSheet<T extends string>({ tabs, tab, onTabChange, snap, onSnapChange, label, tabsLabel, handleLabel, children, header, className }: BottomSheetProps<T>) {
  const [dragOffset, setDragOffset] = useState(0);
  const dragStart = useRef<number | null>(null);

  const onPointerDown = (event: PointerEvent<HTMLButtonElement>) => {
    dragStart.current = event.clientY;
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const onPointerMove = (event: PointerEvent<HTMLButtonElement>) => {
    if (dragStart.current === null) return;

    setDragOffset(event.clientY - dragStart.current);
  };

  const onPointerUp = (event: PointerEvent<HTMLButtonElement>) => {
    if (dragStart.current === null) return;

    const delta = event.clientY - dragStart.current;

    dragStart.current = null;
    setDragOffset(0);
    onSnapChange(snapFromDrag(snap, delta));
  };

  const onHandleKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === "ArrowUp") {
      event.preventDefault();
      onSnapChange(nextSnap(snap, 1));
    } else if (event.key === "ArrowDown") {
      event.preventDefault();
      onSnapChange(nextSnap(snap, -1));
    } else if (event.key === "Escape") {
      event.preventDefault();
      onSnapChange("peek");
    } else if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onSnapChange(snap === "peek" ? "half" : "peek");
    }
  };

  return (
    <section className={["fxmap-sheet", `fxmap-sheet-${snap}`, dragOffset !== 0 ? "fxmap-sheet-dragging" : "", className ?? ""].filter(Boolean).join(" ")} role="dialog" aria-label={label} data-fxmap="bottom-sheet" data-snap={snap} style={dragOffset !== 0 ? { transform: `translateY(${Math.max(0, dragOffset)}px)` } : undefined}>
      <button type="button" className="fxmap-sheet-handle" aria-label={handleLabel} aria-valuetext={snap} data-fxmap-action="sheet-handle" onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={onPointerUp} onPointerCancel={onPointerUp} onKeyDown={onHandleKeyDown}>
        <span className="fxmap-sheet-grip" aria-hidden="true" />
      </button>
      {header !== undefined ? <div className="fxmap-sheet-header">{header}</div> : null}
      <Tabs tabs={tabs} value={tab} onChange={onTabChange} label={tabsLabel} fill dataFxmap="sheet-tabs">
        {(active, panelProps) => (
          <div {...panelProps} className="fxmap-sheet-body">
            {children(active)}
          </div>
        )}
      </Tabs>
    </section>
  );
}
